import * as React from 'react';

/**
 * Third party libs
 */
import moment from 'moment';
import 'moment/locale/es';
moment.locale('es'); // Set Moment.js to use Spanish locale

/**
 * MUI Components
 */
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Divider from '@mui/material/Divider';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

/**
 * MUI Icons
 */
import EditIcon from '@mui/icons-material/Edit';
import DoneIcon from '@mui/icons-material/Done';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import WarningIcon from '@mui/icons-material/Warning';

/**
 * Hooks
 */
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const style = {
    list: { width: '100%', bgcolor: 'background.paper', marginBottom: '100px' }
};

/**
 * Displays a list of reports
 * @param {object} props reports: List of reports
 * @returns the component
 */
export default function ListReports({ reports }) {

    const navigate = useNavigate();
    const { t } = useTranslation();

    const handleEditReport = (report) => {
        console.log('Loading work report with ID', report.id);
        navigate(`/work-report/edit/${report.id}`);
    };

    /**
     * Checks if the report has been validated
     * @param {*} report the report obj
     * @returns {boolean} true if the report is validated
     */
    const isValidated = (report) => {
        return (report.validated_by_uid !== null);
    };

    /**
     * Checks if the report needs changes
     * @param {object} report The report obj
     * @returns {boolean} true if the report needs changes
     */
    const needChanges = (report) => {
        if (report && report.needs_changes) {
            return (report.needs_changes);
        }
        return false;
    }

    /**
     * Checks if the report can be edited
     * @param {*} report the report obj
     * @returns {boolean} true if the report can be edited
     */
    const canEdit = (report) => {
        return !isValidated(report);
    };

    const ValidationIcon = (report) => {
        if (isValidated(report)) {
            return <DoneAllIcon color='success' />;
        } else {
            return <DoneIcon color='success' />;
        }
    };

    const NeedsChangesIcon = (report) => {
        if (needChanges(report)) {
            return <WarningIcon color='warning' />;
        }
    }

    return (
        <List sx={style.list}>
            {reports.map(report => {
                return (
                    <>
                        <ListItem alignItems="flex-start"
                            secondaryAction={
                                canEdit(report) && <IconButton onClick={() => { handleEditReport(report) }} edge="end" aria-label="view">
                                    <EditIcon />
                                </IconButton>
                            }>
                            <div style={{ marginRight: 10 }}>
                                {ValidationIcon(report)}
                            </div>
                            <ListItemText
                                primary={report.work?.name}
                                secondary={
                                    <>
                                        <Box>
                                            <Chip label={'ID ' + report.id} sx={{ marginRight: 1 }} />
                                        </Box>
                                        <Box>
                                            <Typography
                                                sx={{ display: 'inline' }}
                                                component="span"
                                                variant="body2"
                                                color="text.primary"
                                            >
                                                {report.work?.kind_name}
                                            </Typography>
                                            {" - "} {moment(report.created_at).format('DD/MM/YYYY, h:mm:ss a')}
                                        </Box>
                                        {needChanges(report) && (
                                            <Box>
                                                {NeedsChangesIcon(report)}
                                                <Typography
                                                    sx={{ display: 'inline', marginLeft: 1 }}
                                                    component="p"
                                                    variant="body2"
                                                    color="error"
                                                >
                                                    {t('This work report needs changes. Please, review it.')}
                                                </Typography>
                                            </Box>
                                        )}

                                    </>
                                }
                            />
                        </ListItem>
                        <Divider sx={{ marginLeft: '50px' }} variant="inset" component="li" />
                    </>
                );
            })}
        </List>
    );
}